import { Pagination, PaginationItem } from "@mui/material";
import { DisplayParams } from "../types/classes/DisplayParams";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

/**
 * DirectoryPagination Component
 * 
 * This component creates pagination for the member directory results.
 * It handles the pagination logic and updates the display parameters when the page changes.
 * 
 * Props:
 * - pageCount: The total number of pages.
 * - displayParams: An object of type DisplayParams containing the current display parameters.
 * - onPageChange: A function to handle the page change event.
 * 
 * Example usage:
 * ```tsx
 * <DirectoryPagination pageCount={10} displayParams={displayParams} onPageChange={handlePageChange} />
 * ```
 * 
 * @param {number} pageCount - The total number of pages.
 * @param {DisplayParams} displayParams - The current display parameters.
 * @param {(displayParams: DisplayParams) => void} onPageChange - The function to handle the page change event.
 * @returns {JSX.Element} The pagination component for the directory results.
 */
export function DirectoryPagination({ pageCount, displayParams, onPageChange }: {pageCount: number, displayParams: DisplayParams, onPageChange: (displayParams: DisplayParams) => void}) {

    const handleChange = (_: unknown, value: number) => {
        const newDisplayParams: DisplayParams = new DisplayParams();
        newDisplayParams.Page = value;
        newDisplayParams.ShowDetailFlag = true;
        onPageChange(newDisplayParams);
    };

    return (
        <Pagination size="medium" count={pageCount} page={displayParams.Page} onChange={handleChange} variant="outlined" 
            showFirstButton showLastButton
            renderItem={(item) => (
                <PaginationItem
                    slots={{ 
                        first: KeyboardDoubleArrowLeftIcon,
                        last: KeyboardDoubleArrowRightIcon
                    }}
                    {...item}
                />
            )}
        />
    );
}